import { useRef } from 'react';
import { Mic, Square } from 'lucide-react';
import { useDictation } from '../native/dictation';

interface MicButtonProps {
  /** Current field value. */
  value: string;
  /** Receives the value with dictated text appended. */
  onChange: (next: string) => void;
  language?: string;
  className?: string;
}

function revealCaret(field: HTMLTextAreaElement | HTMLInputElement) {
  const len = field.value.length;
  try {
    field.focus();
    field.setSelectionRange(len, len);
  } catch {
    // Some input types reject setSelectionRange.
  }
  field.scrollTop = field.scrollHeight;
}

/**
 * A small mic affordance for dictating into a text field. Renders nothing when
 * dictation isn't available (e.g. an iOS web build), so callers can drop it in
 * unconditionally. Dictated text is appended to whatever was in the field when
 * the user started talking.
 */
export default function MicButton({ value, onChange, language, className = '' }: MicButtonProps) {
  const baseRef = useRef('');
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { supported, listening, toggle } = useDictation((sessionText) => {
    const base = baseRef.current;
    onChange(base ? `${base.replace(/\s+$/, '')} ${sessionText}` : sessionText);
    requestAnimationFrame(() => {
      const root = buttonRef.current?.parentElement;
      const field = root?.querySelector('textarea, input') as
        | HTMLTextAreaElement
        | HTMLInputElement
        | null;
      if (field) revealCaret(field);
    });
  }, language);

  if (!supported) return null;

  const handleClick = () => {
    if (!listening) baseRef.current = value || '';
    toggle();
  };

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={handleClick}
      aria-pressed={listening}
      aria-label={listening ? 'Stop dictation' : 'Dictate your answer'}
      title={listening ? 'Stop dictation' : 'Dictate your answer'}
      className={`p-2 pointer-coarse:p-2.5 rounded-full border shadow-sm transition ${
        listening
          ? 'bg-red-50 border-red-200 text-red-600 animate-pulse'
          : 'bg-white/90 border-gray-200 text-gray-500 hover:text-navy hover:border-navy'
      } ${className}`}
    >
      {listening ? <Square size={16} fill="currentColor" /> : <Mic size={16} />}
    </button>
  );
}
